import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMyPageDashboard } from '../../hooks/useMyPageDashboard';
import { generateNarrativeReport } from '../../data/reportGenerator';

const TotalAnalysis = () => {
    const navigate = useNavigate();
    const { loading, stats, faceResults, loggedIn } = useMyPageDashboard();

    // Pagination State
    const [currentPageIdx, setCurrentPageIdx] = useState(0);

    // Debug Logs
    useEffect(() => {
        // Reset page index when data changes to avoid out-of-bounds
        setCurrentPageIdx(0);
    }, [stats, faceResults]);

    if (loading) return <div style={styles.container}>데이터 분석 중...</div>;

    // Data Preparation
    const mega = stats?.mega;
    const latestFace = faceResults.length > 0 ? faceResults[0] : null;

    // Safe Report Generation
    let reportPages = null;
    let errorMsg = null;

    try {
        if (mega || latestFace) {
            reportPages = generateNarrativeReport(mega, latestFace, stats);
        }
    } catch (e) {
        console.error("GEN ERROR:", e);
        errorMsg = e.toString();
    }

    // Pagination Handlers
    const handleNext = () => {
        if (reportPages && currentPageIdx < reportPages.length - 1) {
            setCurrentPageIdx(prev => prev + 1);
            // Safe scroll
            try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (e) { }
        }
    };

    const handlePrev = () => {
        if (currentPageIdx > 0) {
            setCurrentPageIdx(prev => prev - 1);
            // Safe scroll
            try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (e) { }
        }
    };

    // Error View
    if (errorMsg) {
        return (
            <div style={styles.container}>
                <div style={styles.content}>
                    <h1 style={{ color: 'red' }}>분석 로직 오류</h1>
                    <p style={{ color: '#cbd5e0' }}>{errorMsg}</p>
                    <button onClick={() => navigate('/mypage')} style={styles.backButton}>돌아가기</button>
                </div>
            </div>
        );
    }

    // Safe Current Page access
    const currentPage = (reportPages && reportPages.length > 0 && reportPages[currentPageIdx])
        ? reportPages[currentPageIdx]
        : null;

    return (
        <div style={styles.container}>
            <div style={styles.content}>
                <button onClick={() => navigate('/mypage')} style={styles.backButton}>← 마이페이지로</button>

                <h1 style={styles.title}>종합 분석 리포트</h1>
                <p style={styles.subtitle}>{loggedIn ? '회원님의 모든 검사 결과를 통합하여 분석했습니다.' : '임시 저장된 결과를 바탕으로 분석했습니다.'}</p>

                {reportPages && Array.isArray(reportPages) && reportPages.length > 0 ? (
                    <div style={styles.reportLayout}>

                        {/* 1. Progress Bar */}
                        <div style={styles.progressContainer}>
                            <div style={styles.progressTrack}>
                                <div style={{
                                    ...styles.progressBar,
                                    width: `${((currentPageIdx + 1) / reportPages.length) * 100}%`,
                                    backgroundColor: currentPage ? currentPage.color : '#60a5fa'
                                }}></div>
                            </div>
                            <div style={styles.pageIndicator}>
                                {currentPageIdx + 1} / {reportPages.length}
                            </div>
                        </div>

                        {/* 2. Page Content Card */}
                        {currentPage ? (
                            <div style={{
                                ...styles.pageCard,
                                borderTop: `4px solid ${currentPage.color || '#fff'}`
                            }}>
                                <div style={styles.pageHeader}>
                                    <span style={{
                                        ...styles.pageBadge,
                                        backgroundColor: currentPage.color || '#ccc'
                                    }}>PAGE {currentPage.id}</span>
                                    <h2 style={styles.pageTitle}>{currentPage.title}</h2>
                                </div>
                                <h3 style={styles.pageSubtitle}>{currentPage.subtitle}</h3>
                                <div style={styles.divider}></div>
                                <div style={styles.pageContent}>
                                    {currentPage.content && typeof currentPage.content === 'string' ? (
                                        currentPage.content.split('\n\n').map((para, pIdx) => (
                                            <p key={pIdx} style={{ marginBottom: '1rem' }}>
                                                {para.split('**').map((part, i) =>
                                                    i % 2 === 1 ? <strong key={i} style={{ color: currentPage.color || '#fff' }}>{part}</strong> : part
                                                )}
                                            </p>
                                        ))
                                    ) : (
                                        <p>내용이 없습니다.</p>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <div style={styles.empty}>페이지를 불러올 수 없습니다.</div>
                        )}

                        {/* 3. Navigation Buttons */}
                        <div style={styles.navigation}>
                            <button
                                onClick={handlePrev}
                                disabled={currentPageIdx === 0}
                                style={{
                                    ...styles.navButton,
                                    opacity: currentPageIdx === 0 ? 0.3 : 1,
                                    pointerEvents: currentPageIdx === 0 ? 'none' : 'auto'
                                }}
                            >
                                &lt; 이전
                            </button>

                            <button
                                onClick={handleNext}
                                disabled={currentPageIdx === reportPages.length - 1}
                                style={{
                                    ...styles.navButton,
                                    opacity: currentPageIdx === reportPages.length - 1 ? 0.3 : 1,
                                    pointerEvents: currentPageIdx === reportPages.length - 1 ? 'none' : 'auto'
                                }}
                            >
                                다음 &gt;
                            </button>
                        </div>

                    </div>
                ) : (
                    <div style={styles.empty}>
                        <p>분석할 데이터가 충분하지 않습니다.</p>
                        <p>메가 시너지 테스트와 얼굴 분석을 먼저 진행해주세요.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

const styles = {
    container: {
        minHeight: '100vh',
        backgroundColor: '#0f172a',
        color: 'white',
        padding: '2rem',
        display: 'flex',
        justifyContent: 'center',
    },
    content: {
        width: '100%',
        maxWidth: '1000px',
    },
    backButton: {
        background: 'transparent',
        border: 'none',
        color: '#94a3b8',
        cursor: 'pointer',
        fontSize: '1rem',
        marginBottom: '1rem',
    },
    title: {
        fontSize: '2.2rem',
        fontWeight: 'bold',
        marginBottom: '0.5rem',
        textAlign: 'center',
        background: 'linear-gradient(to right, #fbbf24, #f87171)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
    },
    subtitle: {
        textAlign: 'center',
        color: '#94a3b8',
        marginBottom: '3rem',
    },

    // Pagination & Layout
    reportLayout: {
        display: 'flex',
        flexDirection: 'column',
        gap: '2rem',
    },
    progressContainer: {
        display: 'flex',
        alignItems: 'center',
        gap: '1rem',
        marginBottom: '0.5rem',
    },
    progressTrack: {
        flex: 1,
        height: '8px',
        backgroundColor: 'rgba(255,255,255,0.1)',
        borderRadius: '4px',
        overflow: 'hidden',
    },
    progressBar: {
        height: '100%',
        borderRadius: '4px',
        transition: 'all 0.4s ease',
    },
    pageIndicator: {
        color: '#cbd5e1',
        fontWeight: 'bold',
        fontFamily: 'monospace',
    },

    // Card Styles
    pageCard: {
        backgroundColor: 'rgba(30, 41, 59, 0.5)',
        borderRadius: '24px',
        padding: '3rem',
        border: '1px solid rgba(255, 255, 255, 0.05)',
        boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
        transition: 'all 0.3s ease-in-out',
        minHeight: '500px',
    },
    pageHeader: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '1rem',
        gap: '1rem',
        flexWrap: 'wrap',
    },
    pageBadge: {
        padding: '0.4rem 1rem',
        borderRadius: '20px',
        color: '#1e293b',
        fontWeight: '900',
        fontSize: '0.9rem',
        letterSpacing: '1px',
    },
    pageTitle: {
        fontSize: '1.8rem',
        fontWeight: 'bold',
        color: 'white',
        margin: 0,
    },
    pageSubtitle: {
        fontSize: '1.4rem',
        color: '#cbd5e1',
        fontWeight: 'normal',
        marginBottom: '2rem',
        marginTop: 0,
    },
    divider: {
        height: '1px',
        background: 'linear-gradient(to right, rgba(255,255,255,0.2), transparent)',
        marginBottom: '2rem',
    },
    pageContent: {
        fontSize: '1.1rem',
        lineHeight: '1.8',
        color: '#e2e8f0',
        whiteSpace: 'pre-wrap',
        fontFamily: "'Pretendard', sans-serif",
    },

    // Navigation
    navigation: {
        display: 'flex',
        justifyContent: 'space-between',
        marginTop: '1rem',
    },
    navButton: {
        padding: '1rem 2.5rem',
        fontSize: '1.1rem',
        fontWeight: 'bold',
        color: 'white',
        backgroundColor: 'rgba(255, 255, 255, 0.1)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },

    empty: {
        textAlign: 'center',
        padding: '5rem 0',
        color: '#94a3b8',
        fontSize: '1.2rem',
    }
};

export default TotalAnalysis;
